import React, { useContext, useEffect, useRef, useState } from 'react';
import { GridLoader } from 'react-spinners';
import { useSubscription, MessageServiceConstants } from '@/core/messaging/export';
import { Article } from '@/modules/articles/model/article';
import theme from '@/shared/theme/index.module.scss';
import { articleApi, sectionApi } from '@/modules/articles/api';
import { Section } from '@/modules/articles/model/section';
import { WidgetContainer, WidgetContainerContext } from '@/core/widgets/export';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useWidgetNotifications } from '@/core/notifications/export';
import { SectionFilterPreferences } from '@/modules/articles/components/scrollers/section-filters';
import { AdvancedFilterPreferences } from '@/modules/articles/components/scrollers/advanced-filter-categories';
import { BaseScrollerState } from '@/modules/articles/widgets/scrollers/base-scroller';
import { FilterGroup, qCode } from '@/modules/articles/model/concept-item';
import { VisibleGroup } from '@/modules/articles/components/scrollers/visible-group';
import { authService, useSessionId } from '@/core/auth/export';
import { analyticsService, AnalyticsEvent, AnalyticsTypes } from '@/shared/analytics/export';

interface ScrollerProps {
  section: string[];
  articles?: Article[];
  dark?: boolean;
  title?: JSX.Element;
  onArticlesChange: (articles?: Article[]) => void;
  onBatchLoad: (articles: Article[]) => void;
  filters?: FilterGroup[];
}

type InfiniScrollerState = BaseScrollerState;

const pageSize = 10;
// Stable fallbacks — a fresh `?? {}` literal on every render would break referential
// equality in effect dependency arrays and cause needless refetching.
const EMPTY_EXCLUDED_ITEMS: Record<string, string[]> = {};
const EMPTY_REQUIRED_INCLUDES: Record<string, boolean> = {};

export default function InfiniScroller(props: React.PropsWithChildren<ScrollerProps>) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [offset, setOffset] = useState<number>(0);
  const prevArticlesLength = useRef(articles.length);
  const subject = authService.getSubjectData();
  const sessionId = useSessionId();
  // eslint-disable-next-line
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [sections, setSections] = useState<Section[]>();
  const [includedSections, setIncludedSections] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const widgetContainer = useContext<WidgetContainer>(WidgetContainerContext);
  const { sendNotification } = useWidgetNotifications();
  const advancedFilterPreferences = widgetContainer.getPreferences<AdvancedFilterPreferences>();
  const excludedItemsByGroup =
    advancedFilterPreferences?.excludedItemsByGroup ?? EMPTY_EXCLUDED_ITEMS;
  const activeRequiredIncludes =
    advancedFilterPreferences?.activeRequiredIncludes ?? EMPTY_REQUIRED_INCLUDES;
  const [hasNewArticles, setHasNewArticles] = useState<boolean>(false);
  const [scrollPosition, setScrollPosition] = useState<{
    height: number;
    scrollPosition: number;
    visibleHeight: number;
    scrollTop: number;
  }>();

  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const prevVisibleGroupsRef = useRef<Set<string>>(new Set());

  const resolveVisibleGroups = (filters?: FilterGroup[]): VisibleGroup[] | undefined => {
    if (!filters) return undefined;

    const result: VisibleGroup[] = [];
    filters.forEach(group => {
      const sectionsForGroup = group.visible || [];
      const groupVisible =
        sectionsForGroup.length === 0 || sectionsForGroup.some(s => includedSections.includes(s));
      if (!groupVisible) return;
      if ((group.items || []).length === 0) return;
      result.push({
        title: group.title,
        items: group.items,
        requiredInclude: group.requiredInclude,
      });
    });
    return result.length > 0 ? result : undefined;
  };

  const visibleGroups = resolveVisibleGroups(props.filters);

  const fetchData = (offset: number, oldArticles: Article[], ignoreFilters = false) => {
    setLoading(true);
    setOffset(offset);
    setArticles(oldArticles);

    if (includedSections.length < 1) {
      setHasMore(false);
      setLoading(false);
      return;
    }

    if (!sections) {
      setHasMore(false);
      setLoading(false);
      return;
    }

    const excludeSubjects: string[] = [];
    const excludeGenres: string[] = [];
    if (!ignoreFilters && visibleGroups) {
      visibleGroups.forEach(({ title: group, items }) => {
        if (activeRequiredIncludes[group]) return;
        const excludedQCodes = new Set(excludedItemsByGroup[group] || []);
        items.forEach(item => {
          const qc = qCode(item);
          if (excludedQCodes.has(qc)) {
            if (item.type === 'SUBJECT') excludeSubjects.push(qc);
            else if (item.type === 'GENRE') excludeGenres.push(qc);
          }
        });
      });
    }

    const includeSubjects: string[] = [];
    const includeGenres: string[] = [];
    if (!ignoreFilters && visibleGroups) {
      visibleGroups.forEach(({ title: group, requiredInclude }) => {
        if (!requiredInclude) return;
        if (!activeRequiredIncludes[group]) return;
        const qc = qCode(requiredInclude.item);
        if (requiredInclude.item.type === 'SUBJECT') includeSubjects.push(qc);
        else if (requiredInclude.item.type === 'GENRE') includeGenres.push(qc);
      });
    }

    articleApi
      .getArticlesBySection(
        [
          // eslint-disable-next-line
          ...includedSections.filter(value => sections.find(s => s.code == value)),
        ],
        {
          page: Math.ceil(offset / pageSize),
          limit: pageSize,
          excludeSubjects,
          excludeGenres,
          includeSubjects,
          includeGenres,
          includeSubjectsType: includeSubjects.length > 0 ? 'and' : undefined,
          includeGenresType: includeGenres.length > 0 ? 'and' : undefined,
        },
      )
      .then(
        value => {
          mergeArticles(oldArticles, value.content);
          props.onBatchLoad(value.content);

          setForbidden(false);
          setHasMore(Math.ceil(offset / pageSize) + 1 < value.totalPages);
          setLoading(false);
        },
        reason => {
          setForbidden(true);
          setHasMore(false);
          setLoading(false);
        },
      );
  };

  const mergeArticles = (oldArticles: Article[], newArticles: Article[], append = true) => {
    let a = oldArticles;

    newArticles.forEach(article => {
      const i = a?.findIndex(value => value.uri === article.uri);

      if (i == null || i < 0) {
        a.unshift(article);

        if (!append) a.pop();
      } else {
        a[i] = article;
      }
    });

    a = a.sort((a1, b) => new Date(b.firstcreated).getTime() - new Date(a1.firstcreated).getTime());

    setArticles([...a]);
  };

  useSubscription<Article>(
    MessageServiceConstants.NEWS_ARTICLE_TOPIC,
    (article: Article, headers) => {
      const sections: string[] = JSON.parse(headers.sections);

      if (includedSections.some(value => sections.includes(value))) {
        mergeArticles(articles, [article], false);

        sendNotification({
          correlationId: `scroller-${article.uri}`,
          title: 'New Article',
          description: article.headline,
          durationMillis: 300_000,
          date: new Date(),
        });
      }
    },
  );

  useEffect(() => {
    setOffset(0);
    setArticles([]);
    fetchData(offset, []);

    Promise.allSettled(props.section.map(sectionApi.getSectionByCode)).then(value => {
      const resolved = value
        // eslint-disable-next-line
        .filter(r => r.status == 'fulfilled')

        .map(r => (r as PromiseFulfilledResult<Awaited<Section>>).value)
        .filter(s => s.flags.authorized);

      setSections(resolved);
      // Published so the widget's header Filter button (a separate part of the
      // tree) can render pill labels without re-fetching section metadata.
      widgetContainer.updateState<InfiniScrollerState>({ sections: resolved });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.section]);

  useEffect(() => {
    props.onArticlesChange(articles);
    // eslint-disable-next-line
  }, [articles]);

  useEffect(() => {
    const currentKeys = new Set((visibleGroups || []).map(g => g.title));
    const prevKeys = prevVisibleGroupsRef.current;
    const hiddenGroups = Array.from(prevKeys).filter(k => !currentKeys.has(k));

    if (hiddenGroups.length > 0) {
      const nextExcluded = { ...excludedItemsByGroup };
      const nextRequired = { ...activeRequiredIncludes };
      hiddenGroups.forEach(k => {
        delete nextExcluded[k];
        delete nextRequired[k];
      });
      widgetContainer.updatePreferences<AdvancedFilterPreferences>({
        excludedItemsByGroup: nextExcluded,
        activeRequiredIncludes: nextRequired,
      });
    }
    prevVisibleGroupsRef.current = currentKeys;

    // Published so the widget's header Filter button (a separate part of the
    // tree) can render the category rows without recomputing filter visibility.
    widgetContainer.updateState<InfiniScrollerState>({ visibleGroups });
    fetchData(0, []);
    scrollToTop();
    // eslint-disable-next-line
  }, [includedSections, props.filters]);

  useEffect(() => {
    fetchData(0, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludedItemsByGroup, activeRequiredIncludes]);

  const persistedIncludedSections =
    widgetContainer.getPreferences<SectionFilterPreferences>()?.includedSections;

  useEffect(() => {
    if (!sections) return;

    setIncludedSections(
      persistedIncludedSections && persistedIncludedSections.length > 0
        ? persistedIncludedSections
        : sections.map(value => value.code),
    );
  }, [sections, persistedIncludedSections]);

  useEffect(() => {
    if (articles.length > 0) {
      if (new Date().getTime() - new Date(articles[0].firstcreated).getTime() < 300_000) {
        setHasNewArticles(true);
      } else setHasNewArticles(false);

      if (
        !scrollBoxRef.current ||
        loading ||
        !articles?.length ||
        prevArticlesLength.current === articles.length
      )
        return;

      prevArticlesLength.current = articles.length;
      const scrollContainer = scrollBoxRef.current;
      const loaderDiv = scrollBoxRef.current?.querySelector(
        '#grid-loader-container',
      ) as HTMLElement | null;

      if (loaderDiv) {
        const loaderRect = loaderDiv.getBoundingClientRect();

        const distance = loaderRect.top - scrollContainer.clientHeight;

        if (distance < 30) {
          loadIfNearBottom(loaderRect.top, scrollContainer.clientHeight);
        }
      }
    }
    // eslint-disable-next-line
  }, [articles]);

  const loadIfNearBottom = (height: number, scrollPosition: number) => {
    if (loading) {
      console.log('Already loading....');
      return;
    }

    if (scrollPosition >= height - 100) {
      console.log('Loading from scroll event', articles.length, offset);
      if (offset > 0) {
        analyticsService.recordAnalyticsEvent(AnalyticsEvent.SCROLL_DOWN_TRIGGERED, {
          subject,
          object: {
            type: AnalyticsTypes.WIDGET,
            id: widgetContainer.widgetId,
          },
          session: {
            id: sessionId,
          },
        });
      }
      fetchData(offset + pageSize, articles);
    }
  };

  useEffect(() => {
    if (scrollPosition) loadIfNearBottom(scrollPosition.height, scrollPosition.scrollPosition);
    // eslint-disable-next-line
  }, [scrollPosition]);

  const scrollToTop = () => {
    const current = scrollBoxRef.current;
    if (current) current.scrollTo({ top: 0 });
  };

  return (
    <div className={'container-element h-100 '}>
      <div className={'h-100 d-flex flex-column container-responsive-text '}>
        {loading && articles.length === 0 && (
          <div style={{ textAlign: 'center' }} className={'mt-5'}>
            <GridLoader color={theme.info} />
          </div>
        )}

        {!loading && articles.length === 0 && (
          <div style={{ textAlign: 'center' }} className={'mt-5 text-muted'}>
            No content available yet.
          </div>
        )}

        <div
          className={'flex-fill mt-3 overflow-auto '}
          ref={scrollBoxRef}
          onScroll={event => {
            const target = event.target as HTMLDivElement;

            const scrollHeight = target.scrollHeight;
            const scrollPosition = target.scrollTop + target.offsetHeight;
            // eslint-disable-next-line
            if (scrollPosition == scrollHeight) target.scrollTo({ top: target.scrollTop - 110 });

            setScrollPosition({
              scrollPosition,
              height: scrollHeight,
              visibleHeight: target.clientHeight,
              scrollTop: target.scrollTop,
            });
          }}
        >
          {props.children}

          {hasMore && articles.length > 0 && (
            <div style={{ textAlign: 'center' }} className={'mt-5'} id="grid-loader-container">
              <GridLoader color={theme.info} />
            </div>
          )}

          {scrollPosition && scrollPosition.scrollTop > scrollPosition.visibleHeight && (
            <>
              <div className={'position-absolute bottom-0 start-0   '}>
                <div className={'bg-body-secondary '}>
                  {/*  eslint-disable-next-line   */}
                  <a
                    href={''}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      scrollToTop();
                    }}
                  >
                    Top ({articles.length} items) <FontAwesomeIcon icon={faArrowUp} />
                  </a>
                </div>
              </div>
              <div className={'position-absolute bottom-0 end-0'}>
                <div className={'bg-body-secondary'}>
                  {hasNewArticles && (
                    // eslint-disable-next-line
                    <a
                      href={''}
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        scrollToTop();
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowUp} /> New Article Available
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
